import React from 'react';
import CopyButton from './CopyButton';
import MarkdownDisplay from './MarkdownDisplay';

const CompartmentList = ({
  items = [],
  expandedState = {},
  setExpandedState,
  showOnButtonPress = false,
  buttonPressed = true,
  defaultExpanded = true,
  theme = 'emerald', // 'emerald' or 'terminal'
  icon = '📋', // icon or React component
  emptyMessage = '',
  loadingMessage = 'Waiting for content...',
  loading = false
}) => {
  // Don't show if showOnButtonPress is true but button wasn't pressed
  if (showOnButtonPress && !buttonPressed) {
    return null;
  }

  // Theme configurations
  const themeConfig = {
    emerald: {
      containerClass: 'mb-3 bg-emerald-50 rounded-lg border border-emerald-200',
      headerClass: 'px-3 py-1.5 bg-emerald-100 border-b border-emerald-200 cursor-pointer hover:bg-emerald-200 flex items-center justify-between',
      arrowClass: 'text-emerald-600 text-sm font-mono',
      titleClass: 'font-semibold text-emerald-800 text-sm truncate',
      contentClass: 'p-3 bg-white',
      contentTextClass: 'text-gray-700 text-sm whitespace-pre-wrap',
      copyTheme: 'emerald'
    },
    orange: {
      containerClass: 'mb-3 bg-orange-50 rounded-lg border border-orange-200',
      headerClass: 'px-3 py-1.5 bg-orange-100 border-b border-orange-200 cursor-pointer hover:bg-orange-200 flex items-center justify-between',
      arrowClass: 'text-orange-600 text-sm font-mono',
      titleClass: 'font-semibold text-orange-800 text-sm truncate',
      contentClass: 'p-3 bg-white',
      contentTextClass: 'text-gray-700 text-sm whitespace-pre-wrap',
      copyTheme: 'orange'
    },
    amber: {
      containerClass: 'mb-3 bg-amber-50 rounded-lg border border-amber-200',
      headerClass: 'px-3 py-1.5 bg-amber-100 border-b border-amber-200 cursor-pointer hover:bg-amber-200 flex items-center justify-between',
      arrowClass: 'text-amber-600 text-sm font-mono',
      titleClass: 'font-semibold text-amber-900 text-sm truncate',
      contentClass: 'p-3 bg-white',
      contentTextClass: 'text-gray-700 text-sm whitespace-pre-wrap',
      copyTheme: 'amber'
    },
    gray: {
      containerClass: 'mb-3 bg-gray-100 rounded-lg border border-gray-300',
      headerClass: 'px-3 py-1.5 bg-gray-200 border-b border-gray-300 cursor-pointer hover:bg-gray-300 flex items-center justify-between',
      arrowClass: 'text-gray-700 text-sm font-mono',
      titleClass: 'font-semibold text-gray-900 text-sm truncate',
      contentClass: 'p-3 bg-white',
      contentTextClass: 'text-gray-700 text-sm whitespace-pre-wrap',
      copyTheme: 'gray'
    },
    terminal: {
      containerClass: 'mb-3 bg-black bg-opacity-30 rounded-lg border border-gray-600',
      headerClass: 'flex items-center justify-between px-3 py-1.5 cursor-pointer hover:bg-gray-800 hover:bg-opacity-50 transition-colors rounded-t-lg',
      arrowClass: 'text-green-400 text-sm font-mono',
      titleClass: 'text-green-300 font-medium text-sm truncate flex items-center',
      contentClass: 'border-t border-gray-600 p-3 bg-black bg-opacity-20',
      contentTextClass: 'text-green-400 text-sm whitespace-pre-wrap',
      copyTheme: 'terminal'
    }
  };

  const config = themeConfig[theme];


  if (items.length === 0) {
    if (loading) {
      return (
        <div className="text-gray-500 text-sm pt-2 italic">
          {loadingMessage}
        </div>
      );
    }
    return null;
  }

  return (
    <div className="pt-2">
      {items.map((item, index) => {
        // Safety checks
        if (!item) {
          console.warn(`Null item at index ${index}`);
          return null;
        }

        if (!item.id || typeof item.id !== 'string') {
          console.warn('Invalid item ID:', item);
          return null;
        }

        if (!item.title || typeof item.title !== 'string') {
          console.warn('Invalid item title:', item);
          return null;
        }

        const isExpanded = defaultExpanded
          ? expandedState[item.id] !== false
          : expandedState[item.id] === true;

        const toggleExpanded = () => {
          if (!item.id) return;
          setExpandedState(prev => {
            const currentState = defaultExpanded
              ? prev[item.id] !== false
              : prev[item.id] === true;
            return {
              ...prev,
              [item.id]: !currentState
            };
          });
        };

        return (
          <div key={item.id} className={config.containerClass}>
            {/* Clickable header */}
            <div className={config.headerClass} onClick={toggleExpanded}>
              <div className="flex items-center gap-2 min-w-0">
                <span className={config.arrowClass + ' flex items-center'} style={{fontSize: '12px'}}>
                  {isExpanded ? '▼' : '▶'}
                </span>
                <span className={config.titleClass + ' flex items-center'}>
                  {icon}<span>&nbsp;</span>{index + 1}. {item.title}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <CopyButton
                  content={item.content || ''}
                  theme={config.copyTheme}
                />
              </div>
            </div>

            {/* Expandable content */}
            {isExpanded && (
              <div className={config.contentClass}>
                <MarkdownDisplay content={item.content || emptyMessage} className={config.contentTextClass} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CompartmentList;